import * as React from 'react'
import { MailOutline, MailRounded } from '@mui/icons-material'
import { Button, CircularProgress, IconButton, Tooltip } from '@mui/material'
import { GridActionsCellItem } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'

import { useAlert } from 'components/Shared/Alert'
import { sendActivationMail } from 'api/Participant/SendActivationMail/Action'
import DesktopOnly from 'lib/UI/DesktopOnly'
import MobileOnly from 'lib/UI/MobileOnly'

type SendActivationMailProps = {
  participantID: string
  variant?: 'icon' | 'button'
}

const SendActivationMail = ({
  participantID,
  variant,
}: SendActivationMailProps) => {
  const [loading, onLoading] = React.useState(false)
  const { showAlert } = useAlert()
  const { t } = useTranslation()
  const labelText = `${t('participant.actions.sendActivationMail.label')}`

  const handelSendActivationClick = async () => {
    onLoading(true)
    const result = await sendActivationMail(participantID)

    onLoading(false)

    if (result && result.code) {
      showAlert({
        title: t('participant.actions.sendActivationMail.error'),
      })

      return
    }

    showAlert({
      type: 'success',
      title: t('participant.actions.sendActivationMail.success'),
    })
  }

  const sendActivationMailButton = (
    <React.Fragment>
      <DesktopOnly>
        <Button
          data-testid="send-activation-mail-btn"
          variant="outlined"
          startIcon={
            loading ? (
              <CircularProgress size={20} />
            ) : (
              <MailRounded fontSize="small" />
            )
          }
          onClick={handelSendActivationClick}
          disabled={loading}
        >
          {labelText}
        </Button>
      </DesktopOnly>

      <MobileOnly>
        <IconButton
          color="primary"
          aria-label={labelText}
          component="span"
          onClick={handelSendActivationClick}
        >
          {loading ? (
            <CircularProgress size={20} />
          ) : (
            <MailRounded fontSize="small" />
          )}
        </IconButton>
      </MobileOnly>
    </React.Fragment>
  )

  const sendActivationMailIconButton = (
    <GridActionsCellItem
      key="activation"
      icon={
        <Tooltip title={labelText}>
          <MailOutline fontSize="small" />
        </Tooltip>
      }
      disabled={loading}
      label={labelText}
      onClick={handelSendActivationClick}
      data-testid="send-activation-mail"
    />
  )

  if (variant === 'button') return sendActivationMailButton

  return sendActivationMailIconButton
}

export default SendActivationMail
